import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { apiError, appUrl, goTo, isOwnUrl, parseHost, routeAfterLogin } from '../lib/host';
import AuthShell from '../components/AuthShell';
import './Auth.css';

// Customer approval (access/approval.py on the server): why a company can't be opened yet.
const NOTICES = {
  pending_approval: 'Your account is still pending review by our team. Once your account is approved, we will notify you by email. You can log in after approval.',
  account_rejected: 'Your account application was not approved. Please contact support@telecalx.com for help.',
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const next = params.get('next');
  const { session, loading: authLoading, login, verifyLoginCode } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  // Sent back here from the company (TenantContext): pending review is a notice, a rejection an error.
  const [error, setError] = useState(() => (params.get('notice') === 'account_rejected' ? NOTICES.account_rejected : ''));
  const [loading, setLoading] = useState(false);
  // Website Control (Super Admin): customer login / sign-up on or off. The server enforces both; this only explains it.
  const [site, setSite] = useState({ login_enabled: true, registration_enabled: true });
  const [staff, setStaff] = useState(() => /\/\/(admin|infra)\./.test(next || '')); // admins can always sign in
  const [challenge, setChallenge] = useState(null); // two-factor: {challenge_id, email_hint}
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false); // authenticator sign-in: typing a backup code instead
  const [message, setMessage] = useState('');
  const [notice, setNotice] = useState(() => (params.get('notice') === 'pending_approval' ? NOTICES.pending_approval : ''));
  const companyHost = parseHost().kind === 'tenant'; // https://<company>.<root>/login, from the registration email

  useEffect(() => {
    authAPI.website().then((res) => setSite(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (authLoading || !session) return;
    const isStaff = session.user.is_super_admin || session.user.is_infra_admin;
    if (companyHost && !isStaff) goTo(next && isOwnUrl(next) ? next : '/');
    else routeAfterLogin(session, navigate, next);
  }, [authLoading, session, navigate, next, companyHost]);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
    setError('');
    setNotice('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(formData.email, formData.password, formData.rememberMe);
      if (res?.two_factor_required) {
        setChallenge(res);
        setLoading(false);
      }
      // otherwise the session effect above performs the redirect
    } catch (err) {
      showLoginError(err, 'Login failed. Please try again.');
      setLoading(false);
    }
  };

  /** A company waiting for approval gets the review notice, not an error. */
  const showLoginError = (err, fallback) => {
    if (err.response?.data?.detail?.code === 'pending_approval') setNotice(apiError(err));
    else setError(apiError(err, fallback));
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await verifyLoginCode(challenge.challenge_id, code);
    } catch (err) {
      const detail = apiError(err, 'That code didn\'t work.');
      showLoginError(err, 'That code didn\'t work.');
      setCode('');
      setLoading(false);
      if (/expired|sign in again|locked/i.test(detail)) setChallenge(null);
    }
  };

  const resend = async () => {
    setError('');
    try {
      const res = await authAPI.resendLoginCode(challenge.challenge_id);
      setMessage(`A new code is on its way (${res.data.remaining} more resend${res.data.remaining === 1 ? '' : 's'}).`);
    } catch (err) {
      setError(apiError(err, 'Couldn\'t send a new code.'));
    }
  };

  if (challenge) {
    const app = challenge.method === 'totp';
    const ready = backup ? code.replace('-', '').length === 8 : code.length === 6;
    const reset = () => { setChallenge(null); setCode(''); setBackup(false); setError(''); setMessage(''); };
    return (
      <AuthShell side="login">
        <h2>Enter your sign-in code.</h2>
        <p className="tcx-sub">
          {app
            ? (backup ? 'Enter one of the backup codes you saved when you set up the authenticator app. Each works once.'
              : 'Two-factor sign-in is on for your account. Open your authenticator app (Google or Microsoft Authenticator) and enter the 6-digit code for TeleCalX.')
            : <>Two-factor sign-in is on for your account. We emailed a 6-digit code to <strong>{challenge.email_hint}</strong>.</>}
        </p>
        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}
        <form onSubmit={submitCode}>
          <div className="form-group">
            <label htmlFor="login-code">{backup ? 'Backup code' : app ? 'Authenticator code' : 'Sign-in code'}</label>
            <input id="login-code" className="tcx-code-input" type="text" inputMode={backup ? 'text' : 'numeric'} autoComplete="one-time-code"
              maxLength={backup ? 9 : 6} placeholder={backup ? 'xxxx-xxxx' : '000000'} value={code} autoFocus disabled={loading} required
              onChange={(e) => {
                setCode(backup ? e.target.value.replace(/[^0-9a-fA-F-]/g, '').slice(0, 9) : e.target.value.replace(/\D/g, '').slice(0, 6));
                setError('');
              }} />
            <small className="tcx-hint">{app ? (backup ? 'Backup codes look like 3f9a-27c1.' : 'A new code appears every 30 seconds.') : 'The code expires in 10 minutes.'}</small>
          </div>
          <button type="submit" disabled={loading || !ready} className="submit-btn">
            {loading ? 'Checking...' : 'Sign in'}
          </button>
        </form>
        <p className="auth-link">
          {app
            ? <button type="button" className="tcx-link-btn" onClick={() => { setBackup(!backup); setCode(''); setError(''); }} disabled={loading}>
                {backup ? 'Use the authenticator app' : 'Use a backup code'}</button>
            : <>Didn't get it? <button type="button" className="tcx-link-btn" onClick={resend} disabled={loading}>Send a new code</button></>}
          {' · '}
          <button type="button" className="tcx-link-btn" onClick={reset}>Use another account</button>
        </p>
      </AuthShell>
    );
  }

  const signUpLink = site.registration_enabled && (
    <p className="auth-link">
      New to TeleCalX? {companyHost ? <a href={appUrl('/register')}>Get started</a> : <Link to="/register">Get started</Link>}
    </p>
  );

  if (!site.login_enabled && !staff) {
    return (
      <AuthShell side="login">
        <h2>Login is temporarily unavailable.</h2>
        <p className="tcx-sub">Customer sign-in to TeleCalX is turned off for now. Please try again later, or contact support@telecalx.com.</p>
        {signUpLink}
        <p className="auth-link">
          <button type="button" className="tcx-link-btn" onClick={() => setStaff(true)}>TeleCalX administrator sign-in</button>
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell side="login">
      <h2>Welcome back.</h2>
      <p className="tcx-sub">Sign in to manage your business communication.</p>

      {!site.login_enabled && <div className="tcx-notice">Customer login is turned off. Only TeleCalX administrators can sign in.</div>}
      {location.state?.success && <div className="success-message">{location.state.success}</div>}
      {notice && <div className="tcx-notice" role="status">{notice}</div>}
      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="email">Email</label>
          <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required disabled={loading} />
        </div>

        <div className="form-group">
          <label htmlFor="password" className="tcx-label-row">
            Password <Link to="/forgot-password" className="tcx-forgot">Forgot password?</Link>
          </label>
          <input type="password" id="password" name="password" value={formData.password} onChange={handleChange} required disabled={loading} />
        </div>

        <div className="checkbox-group">
          <input type="checkbox" id="rememberMe" name="rememberMe" checked={formData.rememberMe} onChange={handleChange} disabled={loading} />
          <label htmlFor="rememberMe">Keep me logged in</label>
        </div>

        <button type="submit" disabled={loading} className="submit-btn">
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      {signUpLink}
    </AuthShell>
  );
}
